import { Vec3, vec3, add, sub, scale, cross, dot, normalize, length, lengthSq, ZERO, UNIT_Y } from '../math/vec3'
import { rotate } from '../math/quat'
import { clamp } from '../math/scalar'
import { AeroSurfaceDef, Shape } from '../vessel/part'
import { Vessel } from '../vessel/vessel'
import { PartCatalog, partDef } from '../vessel/catalog'

export interface PartAeroForce {
  readonly partId: string
  /** forza nel sistema del veicolo, N */
  readonly force: Vec3
  /** punto d'applicazione nel sistema del veicolo */
  readonly point: Vec3
}

/**
 * Coefficiente di forza normale di una superficie piana.
 *
 * Sotto stallo vale il modello di piastra sottile Cn = a·sinα·cosα, che a
 * piccoli angoli degrada nel classico Cn ≈ a·α. Oltre lo stallo il
 * coefficiente non cresce piu e decade dolcemente; la formula e scelta per
 * essere CONTINUA all'angolo di stallo, perche una discontinuita li
 * produrrebbe scatti nel controllo.
 */
/**
 * Area vista di fianco: quella che il flusso trasversale investe davvero.
 * Per un cono e il triangolo, cioe meta del rettangolo circoscritto.
 */
export function plantArea(shape: Shape): number {
  switch (shape.kind) {
    case 'cylinder': return 2 * shape.radius * shape.height
    case 'cone': return shape.radius * shape.height
    case 'box': return shape.y * Math.max(shape.x, shape.z)
  }
}

/**
 * Variazione di sezione lungo l'asse: quanto la parte "si apre" al flusso.
 * Un'ogiva passa da zero alla sezione piena, un cilindro non cambia nulla.
 * E questa la grandezza che governa il termine potenziale di corpo snello.
 */
export function sectionGrowth(shape: Shape): number {
  switch (shape.kind) {
    case 'cone': return Math.PI * shape.radius * shape.radius
    case 'cylinder': return 0
    case 'box': return 0
  }
}

export function normalCoefficient(s: AeroSurfaceDef, alpha: number): number {
  const a = Math.abs(alpha)
  if (a <= s.stallAngle) return s.liftSlope * Math.sin(alpha) * Math.cos(alpha)
  const cnStall = s.liftSlope * Math.sin(s.stallAngle) * Math.cos(s.stallAngle)
  const t = (a - s.stallAngle) / (Math.PI / 2 - s.stallAngle)
  return Math.sign(alpha) * cnStall * (1 - 0.4 * clamp(t, 0, 1))
}

/**
 * Forze aerodinamiche parte per parte.
 * `airflowBody` e la velocita dell'ARIA RISPETTO AL VEICOLO, in sistema veicolo:
 * un razzo che sale verso il proprio +Y vede l'aria arrivare verso -Y.
 *
 * Non esiste da nessuna parte una variabile "stabilita": il centro di pressione
 * e semplicemente il punto d'applicazione della risultante di queste forze.
 *
 * Ogni parte contribuisce con tre termini: la resistenza lungo il flusso, la
 * forza normale se e una superficie portante, e la portanza di corpo se ha un
 * fianco da offrire al flusso trasversale. Quest'ultima e cio che rende
 * instabile un razzo senza alette.
 */
export function aeroForces(
  vessel: Vessel,
  catalog: PartCatalog,
  airflowBody: Vec3,
  density: number,
): PartAeroForce[] {
  const speedSq = lengthSq(airflowBody)
  if (density <= 0 || speedSq <= 0) return []

  const q = 0.5 * density * speedSq       // pressione dinamica
  const dir = normalize(airflowBody)
  const out: PartAeroForce[] = []

  for (const p of vessel.parts) {
    const def = partDef(catalog, p.defId)

    // resistenza: sempre nel verso del flusso
    let force = scale(dir, q * def.cd * def.refArea)

    // forza normale delle superfici portanti
    const s = def.aeroSurface
    if (s) {
      const n = rotate(p.rotation, s.normal)
      const alpha = Math.asin(clamp(dot(dir, n), -1, 1))
      force = add(force, scale(n, q * s.area * normalCoefficient(s, alpha)))
    }

    // PORTANZA DI CORPO (Allen e Perkins): un cilindro investito di traverso
    // non genera solo resistenza, ma anche una forza normale al proprio asse,
    // proporzionale a sin²α. E il termine che rende INSTABILE un razzo senza
    // alette: senza, resterebbe indifferente, che e irrealistico e — in un
    // editor che deve insegnare — diseducativo.
    const kc = def.crossflowFactor ?? 1.2
    if (kc > 0) {
      const asse = rotate(p.rotation, UNIT_Y)
      const cosAlpha = dot(dir, asse)
      const perpendicolare = sub(dir, scale(asse, cosAlpha))
      const sinAlpha = length(perpendicolare)

      if (sinAlpha > 1e-9) {
        const versoNormale = scale(perpendicolare, 1 / sinAlpha)

        // termine viscoso: il fianco che frena il flusso trasversale, ∝ sin²α
        const viscoso = q * plantArea(def.shape) * kc * sinAlpha * sinAlpha

        // TERMINE POTENZIALE di corpo snello: una parte la cui SEZIONE CRESCE
        // lungo l'asse devia il flusso e genera una forza normale ∝ sin2α,
        // molto piu grande di quella viscosa ai piccoli angoli. E il termine
        // che rende destabilizzante un'ogiva: senza, il naso di un razzo
        // sarebbe aerodinamicamente invisibile e nessun razzo capotterebbe.
        const potenziale = q * 2 * sectionGrowth(def.shape) * sinAlpha * Math.abs(cosAlpha)

        force = add(force, scale(versoNormale, viscoso + potenziale))
      }
    }

    out.push({ partId: p.id, force, point: p.position })
  }
  return out
}

export function totalAeroForce(forces: readonly PartAeroForce[]): Vec3 {
  return forces.reduce<Vec3>((acc, f) => add(acc, f.force), ZERO)
}

/** Coppia risultante attorno a un punto, tipicamente il centro di massa. */
export function aeroTorque(forces: readonly PartAeroForce[], about: Vec3): Vec3 {
  return forces.reduce<Vec3>((acc, f) => add(acc, cross(sub(f.point, about), f.force)), ZERO)
}

/**
 * Centro di pressione: la stazione sull'ASSE del veicolo in cui applicare la
 * risultante LATERALE per riprodurre la coppia osservata.
 *
 * Non si usa il punto della retta d'azione piu vicino all'origine: con la
 * resistenza assiale che domina, quel punto collassa verso lo zero
 * qualunque cosa facciano le alette, e il marker diventerebbe inutile.
 *
 * Cercando y tale che (0,y,0) × (Fx,0,Fz) riproduca le componenti laterali
 * della coppia si ottiene y = (τx·Fz − τz·Fx)/(Fx² + Fz²).
 *
 * Serve solo come indicatore per l'editor: la fisica usa direttamente le
 * forze e i loro punti d'applicazione.
 */
export function centerOfPressure(forces: readonly PartAeroForce[]): Vec3 | null {
  if (forces.length === 0) return null
  const F = totalAeroForce(forces)
  const lateral = F.x * F.x + F.z * F.z
  if (lateral < 1e-12) return null
  const tau = aeroTorque(forces, ZERO)
  return vec3(0, (tau.x * F.z - tau.z * F.x) / lateral, 0)
}
